import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { quotesApi } from "@/lib/quotes";
import { QuoteItemsGrid, type QuoteItemRow } from "@/components/quotes/quote-items-grid";
import { ClientPicker, type ClientPickerValue } from "@/components/client-picker";
import { PageContainer } from "@/app/layouts/PageContainer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "@/hooks/use-toast";

// ---------------------------------------------------------------------------
// Step type
// ---------------------------------------------------------------------------

type Step = 1 | 2 | 3;

// ---------------------------------------------------------------------------
// Step indicators
// ---------------------------------------------------------------------------

function StepIndicator({ step }: { step: Step }) {
  const steps = ["Client", "Items", "Review"];
  return (
    <ol className="flex items-center gap-4 mb-6">
      {steps.map((label, idx) => {
        const n = (idx + 1) as Step;
        const active = step === n;
        const done = step > n;
        return (
          <li key={label} className="flex items-center gap-2">
            <span
              className={`inline-flex h-7 w-7 items-center justify-center rounded-full text-sm font-semibold ${
                active
                  ? "bg-primary text-primary-foreground"
                  : done
                  ? "bg-emerald-500 text-white"
                  : "bg-muted text-muted-foreground"
              }`}
            >
              {n}
            </span>
            <span
              className={`text-sm ${active ? "font-semibold" : "text-muted-foreground"}`}
            >
              {label}
            </span>
            {idx < steps.length - 1 && (
              <span className="text-muted-foreground">→</span>
            )}
          </li>
        );
      })}
    </ol>
  );
}

// ---------------------------------------------------------------------------
// NewQuoteWizard
// ---------------------------------------------------------------------------

export function NewQuoteWizard() {
  const nav = useNavigate();
  const [step, setStep] = useState<Step>(1);

  // Step 1 state
  const [client, setClient] = useState<ClientPickerValue | null>(null);
  const [description, setDescription] = useState("");

  // Step 2 state
  const [items, setItems] = useState<QuoteItemRow[]>([]);

  const create = useMutation({
    mutationFn: () =>
      quotesApi.create({
        clientId: client?.clientId ?? null,
        description: description || null,
      }),
    onSuccess: (q) => {
      toast.success(`Draft quote ${q.quoteRef} created.`);
      nav(`/quotes/${q.quoteId}`);
    },
    onError: (err: Error) => toast.error(`Create failed: ${err.message}`),
  });

  // ---------------------------------------------------------------------------
  // Step 1: Client & description
  // ---------------------------------------------------------------------------

  function Step1() {
    return (
      <div className="space-y-4 max-w-lg">
        <div>
          <Label>Client</Label>
          <ClientPicker value={client} onChange={setClient} />
        </div>
        <div>
          <Label htmlFor="nqw-description">Description</Label>
          <Input
            id="nqw-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Brief description of the quote…"
            aria-label="Description"
          />
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Step 2: Items
  // ---------------------------------------------------------------------------

  function Step2() {
    return (
      <div className="space-y-4">
        <div className="rounded-md bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-900">
          BOM-driven pricing ships in the BOM domain plan. For now, enter items manually below.
        </div>
        <QuoteItemsGrid items={items} onChange={setItems} />
        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            setItems((prev) => [
              ...prev,
              {
                quoteItemId: -Date.now(),
                sortOrder: (prev.length + 1) * 10,
                description: "",
                quantity: 1,
                unitPriceExGst: 0,
                lineTotalExGst: 0,
              },
            ])
          }
        >
          + Add row
        </Button>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Step 3: Review
  // ---------------------------------------------------------------------------

  const totalExGst = items.reduce((s, i) => s + i.lineTotalExGst, 0);

  function Step3() {
    return (
      <div className="space-y-4 max-w-lg">
        <dl className="grid grid-cols-2 gap-2 text-sm">
          <dt className="text-muted-foreground">Client</dt>
          <dd>{client?.clientName ?? "—"}</dd>
          <dt className="text-muted-foreground">Description</dt>
          <dd>{description || "—"}</dd>
          <dt className="text-muted-foreground">Items</dt>
          <dd>{items.length} line(s)</dd>
          <dt className="text-muted-foreground">Total ex-GST</dt>
          <dd>${totalExGst.toFixed(2)}</dd>
        </dl>
        <p className="text-xs text-muted-foreground">
          The quote will be saved as a Draft. You can send it after reviewing in the Quote detail page.
        </p>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <PageContainer>
      <h1 className="text-2xl font-semibold mb-4">New Quote</h1>
      <StepIndicator step={step} />

      {step === 1 && <Step1 />}
      {step === 2 && <Step2 />}
      {step === 3 && <Step3 />}

      <div className="mt-8 flex gap-3">
        {step > 1 && (
          <Button variant="outline" onClick={() => setStep((step - 1) as Step)}>
            Back
          </Button>
        )}
        {step < 3 && (
          <Button onClick={() => setStep((step + 1) as Step)}>Next</Button>
        )}
        {step === 3 && (
          <>
            <Button
              onClick={() => create.mutate()}
              disabled={create.isPending}
              aria-label="Save as Draft"
            >
              {create.isPending ? "Saving…" : "Save as Draft"}
            </Button>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span tabIndex={0}>
                    <Button disabled aria-label="Send now">
                      Send now
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>Sending ships in Plan II.</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </>
        )}
      </div>
    </PageContainer>
  );
}
